import {useOrderProcessContext} from '@hconnect/tuc-processes/context/useOrderProcessContext'
import {useConfirmationPin} from '@hconnect/tuc-processes/fields/confirmationPin/useConfirmationPin'
import {Alert, Button} from '@hm/fundamental'
import {Box, Typography} from '@mui/material'
import React, {useState, useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {PinValidationModal} from './PinValidationModal'

/**
 * Warning banner displayed when PIN validation is required.
 * Shows informational text and an "Enter PIN" button.
 * Manages its own modal state and PIN validation logic.
 */
export const PinValidationBanner: React.FC = () => {
  const {t} = useTranslation()
  const {setConfirmationPin} = useConfirmationPin()
  const {processData} = useOrderProcessContext()

  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const handleValidationSuccess = useCallback(
    (pin: string) => {
      setConfirmationPin(pin)
      setIsModalOpen(false)
    },
    [setConfirmationPin]
  )

  return (
    <>
      <Alert
        variant="warning"
        className="mt-3 [&>*:last-child]:flex-1"
        data-test-id="pin-validation-banner"
      >
        <Box sx={{display: 'flex', flexDirection: 'column', gap: '12px', width: '100%'}}>
          <Typography
            sx={{
              fontFamily: 'Inter',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '20px',
              color: '#29333D'
            }}
          >
            {t('confirmationPin.banner.description')}
          </Typography>
          <Button
            variant="secondary"
            onClick={handleOpenModal}
            data-test-id="enter-pin-button"
            className="w-full"
          >
            {t('confirmationPin.banner.enterPinButton')}
          </Button>
        </Box>
      </Alert>

      <PinValidationModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        customerId={processData.customer.customerId}
        onValidationSuccess={handleValidationSuccess}
      />
    </>
  )
}
